import { HTMLAttributes, ReactElement } from 'react';
import { Field, Input, Label, ValidationMessage } from '@digdir/designsystemet-react';
import type { FieldError } from '../../WorkEnvironmentSurveyDetail/assessmentView';
import { BusinessForm, errorFor } from './businessView';

type Props = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  description?: string;
  error?: string;
  type?: 'text' | 'email' | 'tel' | 'number' | 'date';
  autoComplete?: string;
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  className?: string;
};

/** A labelled input with an optional description and error, as every field in the business form */
export const BusinessTextField = ({
  id,
  label,
  value,
  onChange,
  description,
  error,
  type = 'text',
  autoComplete,
  inputMode,
  className,
}: Props): ReactElement => {
  const describedBy = [description && `${id}-description`, error && `${id}-error`].filter(Boolean).join(' ');
  return (
    <Field className={className}>
      <Label htmlFor={id}>{label}</Label>
      {description && <Field.Description id={`${id}-description`}>{description}</Field.Description>}
      <Input
        id={id}
        type={type}
        value={value}
        autoComplete={autoComplete}
        inputMode={inputMode}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy || undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      {error && <ValidationMessage id={`${id}-error`}>{error}</ValidationMessage>}
    </Field>
  );
};

type TextKey = {
  [K in keyof BusinessForm]: BusinessForm[K] extends string ? K : never;
}[keyof BusinessForm];

export type SectionProps = {
  form: BusinessForm;
  onChange: (changes: Partial<BusinessForm>) => void;
  errors: FieldError[];
};

/** The id, value, change handler and error of a text field in the form */
export const bind = (field: TextKey, { form, onChange, errors }: SectionProps) => ({
  id: `business-${field}`,
  value: form[field],
  onChange: (value: string) => onChange({ [field]: value }),
  error: errorFor(errors, `business-${field}`),
});
